import { useTaskStore } from "@core/stores/task.store"
import { SourceService } from "@core/services"

const sourceService = new SourceService()

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Options pour le lancement de la collecte on-demand des données d'une source.
 * La collecte passe par le routing api-metier (`POST /task/update/source`,
 * source→collecteur : un PCE grdf part vers harvest — DEV-527/DEV-967).
 */
export interface UseRebuildDataTaskOptions {
    /**
     * Affiche une notification lors de la progression de la tâche
     * @default true
     */
    notification?: boolean
    /**
     * Supprime automatiquement la tâche du suivi à la fin
     * @default true
     */
    autoDelete?: boolean
    /**
     * Libellé affiché pour la tâche
     * @default "Récupération des données de la source"
     */
    taskname?: string
    /**
     * `true` = reconstruction historique (fenêtre large) ; `false` (défaut) =
     * mise à jour incrémentale depuis la dernière donnée.
     * @default false
     */
    rebuild?: boolean
    /**
     * Callback appelé lorsque la tâche reçoit des données
     */
    onReceived?: () => void
    /**
     * Callback appelé lorsque la tâche réussit
     */
    onSuccess?: (data: unknown) => void
    /**
     * Callback appelé en cas d'erreur de la tâche
     */
    onError?: (error: unknown) => void
}

// ================================
// COMPOSABLE
// ================================

/**
 * Composable pour lancer la collecte on-demand des données d'une source.
 * Centralise le lancement et le suivi de la tâche de reconstruction des données.
 *
 * @param options - Options de configuration (notification, autoDelete, callbacks)
 * @returns Fonction pour lancer la collecte avec un sourceId
 */
export function useRebuildDataTask(options: UseRebuildDataTaskOptions = {}) {
    const taskStore = useTaskStore()

    const {
        notification = true,
        autoDelete = true,
        taskname = "Récupération des données de la source",
        rebuild = false,
        onReceived,
        onSuccess,
        onError,
    } = options

    /**
     * Lance la collecte des données pour une source et en suit la progression.
     * @param sourceId - Identifiant de la source
     */
    async function launchRebuildDataTask(sourceId: string): Promise<void> {
        try {
            // DEV-967 — on passe par le routing api-metier (`/task/update/source`)
            // au lieu d'enfiler `workers2app.getlastdata_updateapp.request` en
            // direct : ce dernier est **no-op** pour les opérateurs migrés harvest
            // (grdf & co). L'endpoint route source→collecteur (PCE grdf → harvest)
            // et renvoie le task_id à suivre (même toast/subscribe).
            const { task_id } = await sourceService.triggerSourceDataUpdate(
                sourceId,
                { rebuild }
            )
            if (!task_id) {
                throw new Error("Aucun task_id renvoyé par la collecte")
            }
            taskStore.followEvent(task_id, task_id, {
                taskname,
                notification,
                autoDelete,
                onReceived,
                onSuccess,
                onError,
            })
        } catch (error) {
            console.error(
                "Erreur lors du lancement de la collecte de données:",
                error
            )
            onError?.(error)
        }
    }

    return { launchRebuildDataTask }
}
